import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {HTMLElement | null} */
  #section = null;

  /** An untransformed marker preserves the header's natural document position. @type {HTMLElement | null} */
  #positionMarker = null;

  #headerHeight = 0;

  #uiLocked = false;

  #uiObserver = new MutationObserver(() => this.#handleUiChange());

  /** @type {number | null} */
  #resizeTimer = null;

  #handleResize = () => {
    // Debounce resize: iOS fires this continuously as browser chrome hides.
    // Only update header height; never recalculate scroll state mid-resize
    // to avoid layout thrashing during the viewport-height animation.
    if (this.#resizeTimer !== null) return;
    this.#resizeTimer = setTimeout(() => {
      this.#resizeTimer = null;
      this.#headerHeight = this.getBoundingClientRect().height;
    }, 150);
  };

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;


  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    this.#headerHeight = height;
    document.body.style.setProperty('--header-height', `${height}px`);
    this.#handleWindowScroll();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky

   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: 1,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      this.#setHeaderData('stickyState', isIntersecting ? 'inactive' : 'active');
      changeMetaThemeColor(this.refs.headerRowTop);
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /** @param {string} key @param {string} value */
  #setHeaderData(key, value) {
    if (this.dataset[key] !== value) this.dataset[key] = value;
  }

  #isUiOpen() {
    return this.matches(':focus-within') ||
      !!this.querySelector('header-drawer > details[open], header-menu[aria-expanded="true"]') ||
      document.body.classList.contains('boost-sd__g-no-scroll') ||
      document.body.classList.contains('megamenu_open') ||
      !!document.querySelector('#search-modal dialog[open]');
  }

  #handleUiChange = () => {
    const locked = this.#isUiOpen();
    this.#uiLocked = locked;
    this.#handleWindowScroll();
  };

  #handleWindowScroll = () => {
    this.#updateScrollState();
  };

  #updateScrollState() {
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'always' && stickyMode !== 'scroll-up') return;

    // Read geometry before changing state; clamp Safari rubber-band overscroll.
    const scroller = document.scrollingElement;
    const rawScrollTop = scroller?.scrollTop ?? 0;
    const maxScrollTop = scroller ? Math.max(0, scroller.scrollHeight - scroller.clientHeight) : 0;
    const scrollTop = Math.max(0, Math.min(rawScrollTop, maxScrollTop));
    const delta = scrollTop - this.#lastScrollTop;

    if (stickyMode === 'always') {
      const atTop = this.getBoundingClientRect().top >= 0;
      this.#setHeaderData('scrollDirection', atTop ? 'none' : delta < 0 ? 'up' : delta > 0 ? 'down' : this.dataset.scrollDirection || 'none');
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#section || !this.#positionMarker) return;
    const naturalTop = this.#positionMarker.getBoundingClientRect().top + rawScrollTop;
    const atTop = scrollTop <= naturalTop;
    const nearTop = scrollTop <= naturalTop + this.#headerHeight;
    const uiOpen = this.#isUiOpen();
    let hidden = this.#section.classList.contains('sticky-hidden');

    if (nearTop || uiOpen || delta < 0) hidden = false;
    else if (delta > 8) hidden = true;

    this.#section.classList.toggle('sticky-hidden', hidden);
    this.#setHeaderData('stickyState', atTop ? 'inactive' : hidden ? 'idle' : 'active');
    this.#setHeaderData('scrollDirection', atTop || hidden ? 'none' : 'up');
    this.#lastScrollTop = scrollTop;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    this.#section = this.closest('.header-section');
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'always') this.#observeStickyPosition();

    if (stickyMode === 'scroll-up' && this.#section) {
      this.#positionMarker = document.createElement('div');
      this.#positionMarker.setAttribute('aria-hidden', 'true');
      this.#positionMarker.style.cssText = 'height:0;pointer-events:none;';
      this.#section.before(this.#positionMarker);
      this.#headerHeight = this.getBoundingClientRect().height;
      this.#uiObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
      this.#uiObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.addEventListener('focusin', this.#handleUiChange);
      document.addEventListener('focusout', this.#handleWindowScroll);
      document.addEventListener('dialog:open', this.#handleUiChange, true);
      document.addEventListener('dialog:close', this.#handleUiChange, true);
    }

    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      window.addEventListener('resize', this.#handleResize, { passive: true });
      this.#section?.classList.add('sticky');
      this.#handleWindowScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#uiObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleResize);
    if (this.#resizeTimer !== null) clearTimeout(this.#resizeTimer);
    document.removeEventListener('focusin', this.#handleUiChange);
    document.removeEventListener('focusout', this.#handleWindowScroll);
    document.removeEventListener('dialog:open', this.#handleUiChange, true);
    document.removeEventListener('dialog:close', this.#handleUiChange, true);

    this.#positionMarker?.remove();
    this.#positionMarker = null;
    this.#section?.classList.remove('sticky', 'sticky-hidden');
    this.#section = null;
    this.#lastScrollTop = 0;
    this.#uiLocked = false;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
